/**
 * ModuleGate - Componente para controlar acceso a módulos
 * 
 * Verifica si el usuario tiene acceso al módulo.
 * Si no tiene acceso, muestra pantalla "Próximamente".
 * Admin siempre puede acceder.
 */
import { useModuleAccess } from "../hooks/useModuleAccess";
import { useAuth } from "../context/AuthContext";
import { Loader2, Lock, Clock, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";

const ModuleGate = ({ moduleId, children, fallbackPath = "/hoy" }) => {
  const { user } = useAuth();
  const { loading, hasAccess, message, isAdminOverride } = useModuleAccess(moduleId);
  const navigate = useNavigate();

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  // Admin override notice
  if (isAdminOverride && user?.role === "admin") {
    return (
      <div className="relative">
        {/* Admin banner */}
        <div className="fixed top-0 left-0 right-0 z-50 bg-purple-600 text-white text-center py-2 text-sm">
          <Lock className="w-4 h-4 inline mr-2" />
          Modo Admin: Este módulo está desactivado para usuarios
        </div>
        <div className="pt-10">
          {children}
        </div>
      </div>
    );
  }

  // No access - show coming soon screen
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex flex-col items-center justify-center p-6 text-center">
        {/* Premium badge */}
        <div className="w-20 h-20 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-6">
          <Clock className="w-10 h-10 text-[#D4AF37]" />
        </div>

        {/* Title */}
        <h1 className="text-2xl font-heading font-bold text-white mb-3">
          Próximamente
        </h1>

        {/* Message */}
        <p className="text-muted-foreground max-w-md mb-6">
          {message || "Esta sección estará disponible próximamente"}
        </p>

        {/* Premium CTA if not premium */}
        {!user?.is_premium && (
          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-4 mb-6 max-w-sm">
            <div className="flex items-center gap-2 text-[#D4AF37] font-semibold mb-2">
              <Sparkles className="w-5 h-5" />
              GOATGYM Premium
            </div>
            <p className="text-sm text-white/70">
              Accede a todas las funcionalidades premium con tu suscripción
            </p>
          </div>
        )}

        {/* Back button */}
        <Button
          onClick={() => navigate(fallbackPath)}
          variant="outline"
          className="border-[#2A2A2A] text-white hover:bg-[#1A1A1A]"
        >
          Volver al inicio
        </Button>
      </div>
    );
  }

  // Has access - render children
  return children;
};

export default ModuleGate;
